// Mirrors apps/ingestion/services._draft_ready_for_approve in the backend.
// One source of truth for "is this draft ready to copy into the live question
// bank?" used by:
//   - ParsedDraftEditor (disables the Store button + shows what's missing)
//   - ParsedDraftsGrid  (preflight check before POST /drafts/bulk-approve)
//
// The backend still validates on every approve; this is purely UX so admins
// see the gate before they click, not after.

import { CORRECTOPTION, QUESTIONDIFFICULTY, isEssaySubject } from './Constants';

// A correct_option string is valid when it parses to a non-empty set of option
// letters (e.g. "A" or "A,C") — mirrors the backend's parse_option_set so
// multi-answer questions aren't wrongly flagged as missing an answer.
function hasValidCorrectSet(correctOption) {
    const parts = (correctOption || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    return parts.length > 0 && parts.every(p => CORRECTOPTION.includes(p));
}

// Present option letters = those whose option_<x> field carries text.
function presentLetters(draft) {
    return CORRECTOPTION.filter(l => (draft?.[`option_${l.toLowerCase()}`] || '').trim());
}

// Text-Completion blanks are valid = 2–3 blanks, each with exactly 3 non-empty choices and a
// correct index in 0–2. Mirrors backend validate_question_shape.
function hasValidBlanks(draft) {
    const blanks = (draft?.metadata && draft.metadata.blanks) || [];
    if (blanks.length < 2 || blanks.length > 3) return false;
    return blanks.every(b => {
        const choices = (b?.choices || []).filter(c => (c || '').trim());
        return choices.length === 3 && Number.isInteger(b?.correct) && b.correct >= 0 && b.correct <= 2;
    });
}

export function getMissingApprovalFields(draft) {
    const missing = [];
    if (!draft?.question_text?.trim()) missing.push('question_text');
    if (!draft?.difficulty || !QUESTIONDIFFICULTY.includes(draft.difficulty)) {
        missing.push('difficulty');
    }
    if (!draft?.level?.trim()) missing.push('level');

    const qtype = draft?.question_type || 'MCQ';
    // TEXT questions and the Essay subject don't need options / a correct set.
    const isText = qtype === 'TEXT';
    const isEssay = isEssaySubject(draft?.subject || '');

    if (qtype === 'TEXT_COMPLETION') {
        // Answers live in metadata.blanks, not correct_option.
        if (!hasValidBlanks(draft)) missing.push('blanks');
        return missing;
    }
    if (qtype === 'BUILD_SENTENCE') {
        // Tiles are the option fields; the answer is metadata.correct_order — an ordered subset
        // of distinct tile indices. It need NOT use every tile: TOEFL "Build a Sentence" includes
        // distractor tiles (e.g. a wrong-agreement verb) left out of the correct sentence.
        const present = presentLetters(draft);
        const order = (draft?.metadata && draft.metadata.correct_order) || [];
        if (present.length < 2) missing.push('word tiles (need ≥2 options)');
        const inRange = order.every(i => Number.isInteger(i) && i >= 0 && i < present.length);
        const distinct = new Set(order).size === order.length;
        if (order.length < 2 || !inRange || !distinct) missing.push('correct word order');
        return missing;
    }
    if (qtype === 'SENTENCE_EQUIVALENCE') {
        if (!draft?.subject?.trim()) missing.push('subject');
        const present = presentLetters(draft);
        const correct = (draft?.correct_option || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
        if (present.length !== 6) missing.push('options (need 6)');
        if (correct.length !== 2) missing.push('correct_option (pick 2)');
        return missing;
    }
    if (qtype === 'MATCHING') {
        // The shared box lives in metadata.match_options; the answer is a set of box letters
        // (may exceed A–H, e.g. word banks A–J), so it is NOT constrained to CORRECTOPTION.
        const box = (draft?.metadata && draft.metadata.match_options) || {};
        const boxKeys = Object.keys(box).filter(k => (box[k] || '').toString().trim());
        const correct = (draft?.correct_option || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
        if (boxKeys.length < 1) missing.push('matching option box (need ≥1)');
        if (correct.length < 1 || !correct.every(l => boxKeys.map(k => k.toUpperCase()).includes(l))) {
            missing.push('correct_option (a box letter)');
        }
        return missing;
    }
    if (!isText && !isEssay) {
        if (!draft?.subject?.trim()) missing.push('subject');
        if (!hasValidCorrectSet(draft?.correct_option)) missing.push('correct_option');
    }
    return missing;
}

export function isDraftReadyForApproval(draft) {
    return getMissingApprovalFields(draft).length === 0;
}
